import type { NewConfirmationFormData } from "../types/new-confirmation.types";

export type ConfirmationValidationKey =
  | "validation_cliente_required"
  | "validation_pagador_required"
  | "validation_monto_invalid"
  | "validation_banco_required"
  | "validation_moneda_required"
  | "validation_fecha_required"
  | "validation_comprobante_required";

export type ConfirmationErrorKey = ConfirmationValidationKey & {
  readonly __brand: "ConfirmationErrorKey";
};

export type NewConfirmationFormErrors = Partial<
  Record<keyof NewConfirmationFormData, string>
>;

export type RawNewConfirmationFormErrors = Partial<
  Record<keyof NewConfirmationFormData, ConfirmationErrorKey>
>;

export interface ValidateNewConfirmationFormOptions {
  requireAmountAndBank?: boolean;
  requireComprobante?: boolean;
  requireClientAndPayer?: boolean;
  /** Exige clienteId y pagadorId (flujo pagos con clientes verificados). */
  requireClientAndPayerIds?: boolean;
}

function asErrorKey(key: ConfirmationValidationKey): ConfirmationErrorKey {
  return key as ConfirmationErrorKey;
}

/** Mismo criterio que `validation_monto_invalid` en `validateNewConfirmationForm`. */
export function isValidMontoValue(monto: string | null | undefined): boolean {
  const montoNum = parseFloat(String(monto).replace(/,/g, "."));
  return !(
    monto === "" ||
    monto == null ||
    isNaN(montoNum) ||
    montoNum <= 0
  );
}

export function validateNewConfirmationForm(
  data: NewConfirmationFormData,
  options?: ValidateNewConfirmationFormOptions
): RawNewConfirmationFormErrors {
  const {
    requireAmountAndBank = true,
    requireComprobante = true,
    requireClientAndPayer = false,
    requireClientAndPayerIds = false,
  } = options ?? {};
  const errors: RawNewConfirmationFormErrors = {};

  if (requireClientAndPayer) {
    if (!data.cliente?.trim()) {
      errors.cliente = asErrorKey("validation_cliente_required");
    }
    if (!data.nombrePagador?.trim()) {
      errors.nombrePagador = asErrorKey("validation_pagador_required");
    }
  }

  if (requireClientAndPayerIds) {
    if (!data.clienteId?.trim()) {
      errors.cliente = asErrorKey("validation_cliente_required");
    }
    if (!data.pagadorId?.trim()) {
      errors.nombrePagador = asErrorKey("validation_pagador_required");
    }
  }

  if (requireAmountAndBank) {
    if (!isValidMontoValue(data.monto)) {
      errors.monto = asErrorKey("validation_monto_invalid");
    }

    if (!data.banco?.trim()) {
      errors.banco = asErrorKey("validation_banco_required");
    }
  }

  if (!data.tipoMoneda?.trim()) {
    errors.tipoMoneda = asErrorKey("validation_moneda_required");
  }

  if (!data.fecha?.trim()) {
    errors.fecha = asErrorKey("validation_fecha_required");
  }

  if (requireComprobante && !data.comprobante) {
    errors.comprobante = asErrorKey("validation_comprobante_required");
  }

  return errors;
}

type ConfirmationsT = (key: ConfirmationValidationKey) => string;

export function translateNewConfirmationErrors(
  t: ConfirmationsT,
  raw: RawNewConfirmationFormErrors
): NewConfirmationFormErrors {
  const out: NewConfirmationFormErrors = {};
  for (const [key, value] of Object.entries(raw) as [keyof NewConfirmationFormData, ConfirmationErrorKey][]) {
    if (value != null && typeof value === "string") {
      out[key] = t(value as ConfirmationValidationKey);
    }
  }
  return out;
}
